/**
 * IxcConfigModal — Sprint 4.2D.
 *
 * Substitui o IntegrationsModal placeholder da Sprint 4.2C com UI completa:
 *   - Configuração (URL, auth mode, credenciais, timeout, TLS)
 *   - Test connection
 *   - Sync buttons (clientes / infra / completo) com summary inline
 *   - Logs recentes
 *
 * Briefing READ-ONLY: nada escrito no IXC.
 *
 * Segurança:
 *   - Campos token/password tipo='password' nunca preenchidos com valor
 *     existente (servidor não devolve credentials decifradas)
 *   - has_credentials=true mostra "✓ Credenciais configuradas" sem expor
 *   - Para trocar credenciais, usuário digita de novo
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import {
  createIxcConfig,
  deleteIxcConfig,
  getIxcConfig,
  listIxcLogs,
  syncIxcCustomers,
  syncIxcFull,
  syncIxcInfra,
  testIxcConnection,
  updateIxcConfig,
} from "../../lib/api.js";
import { t } from "../../i18n/index.js";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtSummary(s) {
  if (!s || typeof s !== "object") return "—";
  const parts = [];
  if (s.fetched != null) parts.push(`${s.fetched} buscados`);
  if (s.inserted != null) parts.push(`${s.inserted} novos`);
  if (s.updated != null) parts.push(`${s.updated} atualizados`);
  if (s.errors != null && s.errors > 0) parts.push(`${s.errors} erros`);
  if (s.geocoding_pending) parts.push(`${s.geocoding_pending} geocoding pending`);
  return parts.join(" · ") || JSON.stringify(s).slice(0, 80);
}

const STATUS_COLOR = {
  success: "text-noc-ok",
  partial: "text-noc-warn",
  failed: "text-noc-err",
  running: "text-noc-accent",
};

export default function IxcConfigModal({ open, onClose, onAfterSync }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state (controlled)
  const [name, setName] = useState("IXC Principal");
  const [baseUrl, setBaseUrl] = useState("");
  const [authMode, setAuthMode] = useState("basic");
  const [token, setToken] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [timeoutSec, setTimeoutSec] = useState(30);
  const [tlsVerify, setTlsVerify] = useState(true);
  const [geocodingEnabled, setGeocodingEnabled] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  // Fix C — só libera o Save depois que getIxcConfig() concluir (evita salvar
  // o default antes do load e regredir auth_mode para "token").
  const [configReady, setConfigReady] = useState(false);

  // Sync state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  // Logs
  const [logs, setLogs] = useState([]);

  // Carregamento inicial
  useEffect(() => {
    if (!open) return;
    setError(null);
    setTestResult(null);
    setLastSync(null);
    setConfigReady(false);
    loadConfig();
    loadLogs();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !loading && !syncing) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, syncing, onClose]);

  if (!open) return null;

  async function loadConfig() {
    setLoading(true);
    try {
      const cfg = await getIxcConfig();
      setConfig(cfg);
      if (cfg) {
        setName(cfg.name || "");
        setBaseUrl(cfg.base_url || "");
        setAuthMode(cfg.auth_mode || "basic");
        setTimeoutSec(cfg.timeout_seconds || 30);
        setTlsVerify(cfg.tls_verify !== false);
        setGeocodingEnabled(!!cfg.geocoding_enabled);
        setEnabled(!!cfg.enabled);
      }
      setToken(""); setUsername(""); setPassword("");
    } catch (e) {
      setError(e?.message || "Erro ao carregar config IXC");
    } finally {
      setLoading(false);
      setConfigReady(true);
    }
  }

  async function loadLogs() {
    try {
      const data = await listIxcLogs({ limit: 10 });
      setLogs(data.logs || []);
    } catch {
      setLogs([]);
    }
  }

  const handleSave = async () => {
    if (!baseUrl.trim()) {
      setError("Base URL é obrigatória.");
      return;
    }
    setLoading(true); setError(null);
    try {
      const creds = {};
      if (authMode === "token" && token) creds.token = token;
      if (authMode === "basic") {
        if (username) creds.username = username;
        if (password) creds.password = password;
      }
      const body = {
        name: name.trim(),
        base_url: baseUrl.trim(),
        auth_mode: authMode,
        credentials: creds,
        timeout_seconds: timeoutSec,
        tls_verify: tlsVerify,
        geocoding_enabled: geocodingEnabled,
        enabled,
      };
      if (config) {
        await updateIxcConfig(body);
      } else {
        await createIxcConfig(body);
      }
      await loadConfig();
      setToken(""); setPassword("");
    } catch (e) {
      setError(e?.message || "Erro ao salvar config");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remover integração IXC? Os customers já sincronizados permanecem no banco.")) return;
    setLoading(true);
    try {
      await deleteIxcConfig();
      setConfig(null);
      setName("IXC Principal"); setBaseUrl(""); setAuthMode("token");
      setToken(""); setUsername(""); setPassword("");
      setTimeoutSec(30); setTlsVerify(true); setGeocodingEnabled(false); setEnabled(false);
    } catch (e) {
      setError(e?.message || "Erro ao remover");
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null); setError(null);
    try {
      const r = await testIxcConnection();
      setTestResult(r);
    } catch (e) {
      setError(e?.message || "Erro no teste");
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async (kind) => {
    setSyncing(true); setLastSync(null); setError(null);
    try {
      let r;
      if (kind === "customers") r = await syncIxcCustomers();
      else if (kind === "infra") r = await syncIxcInfra();
      else r = await syncIxcFull();
      setLastSync({ kind, result: r });
      await loadLogs();
      await loadConfig();
      onAfterSync?.();
    } catch (e) {
      setError(e?.message || `Erro em sync ${kind}`);
    } finally {
      setSyncing(false);
    }
  };

  const ready = !!config && !!config.has_credentials && config.enabled;
  const busy = loading || testing || syncing;

  // Robustez/simetria (MAP-UX): portaliza p/ document.body como
  // ConfirmCriticalAction/BulkRelayRebootModal — imune a transform/filter futuro
  // em .ipwd-fleet-focus (mapa fullscreen). z-[2000] segue acima do mapa e abaixo
  // da confirmação crítica (z-3000/4000).
  return createPortal(
    <div className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="bg-noc-panel border border-noc-purple/30 rounded shadow-2xl w-full max-w-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-noc-purple">🔌 IXC — ReadOnly GIS Sync</h3>
          <button type="button" onClick={onClose} disabled={busy}
            className="text-noc-muted hover:text-noc-text text-lg leading-none disabled:opacity-50">×</button>
        </div>

        <div className="text-[10px] text-noc-purple bg-noc-purple/10 border border-noc-purple/30 rounded p-2">
          <strong>Sprint 4.2D — READ-ONLY.</strong>{" "}
          Nenhuma alteração é feita no IXC. Apenas sync de clientes, infra
          e coordenadas para o Fleet Map. Credenciais são criptografadas
          at-rest (Fernet AES-128).
        </div>

        {error && (
          <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 flex items-start gap-2">
            <span className="flex-1">{error}</span>
            <button type="button" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* ============ Configuração ============ */}
        <div className="space-y-2 bg-noc-bg/30 border border-noc-border rounded p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-noc-text">Configuração</div>
            {config && (
              <div className="text-[10px]">
                <span className={config.has_credentials ? "text-noc-ok" : "text-noc-warn"}>
                  {config.has_credentials ? "✓ Credenciais configuradas (cifradas)" : "⚠ Sem credenciais"}
                </span>
                {" · "}
                <span className={config.enabled ? "text-noc-ok" : "text-noc-muted"}>
                  {config.enabled ? "● enabled" : "○ disabled"}
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="block text-[10px]">
              <span className="text-noc-muted">Nome da integração</span>
              <input value={name} onChange={(e) => setName(e.target.value)}
                placeholder="IXC Principal"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5" />
            </label>
            <label className="block text-[10px]">
              <span className="text-noc-muted">Base URL *</span>
              <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://ixc.example.com"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5 font-mono" />
            </label>
          </div>

          {baseUrl.toLowerCase().includes("/adm.php") && (
            <div className="text-[10px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
              ⚠ A Base URL contém <code>/adm.php</code>. O backend usa só a raiz do
              host (ex.: <code>https://ixc.exemplo.com.br</code>) — recomendado
              remover o <code>/adm.php</code>.
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label className="block text-[10px]">
              <span className="text-noc-muted">Auth mode</span>
              <select value={authMode} onChange={(e) => setAuthMode(e.target.value)}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5">
                <option value="token">Token API</option>
                <option value="basic">Basic (usuário + senha)</option>
              </select>
            </label>
            <label className="block text-[10px]">
              <span className="text-noc-muted">Timeout (segundos)</span>
              <input type="number" value={timeoutSec} onChange={(e) => setTimeoutSec(parseInt(e.target.value || "30", 10))}
                min="1" max="300"
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5 font-mono" />
            </label>
          </div>

          {authMode === "token" && (
            <div className="text-[10px] text-noc-warn bg-noc-warn/10 border border-noc-warn/30 rounded p-2">
              ⚠ Use <strong>Token API</strong> somente se seu IXC aceitar esse modo.
              Para IXC tradicional, use <strong>Basic</strong>.
            </div>
          )}

          {authMode === "token" ? (
            <label className="block text-[10px]">
              <span className="text-noc-muted flex items-center gap-2">
                Token API
                {config?.has_credentials && !token && (
                  <span className="text-[9px] text-noc-muted/70">(configurado; vazio = mantém atual)</span>
                )}
              </span>
              <div className="flex gap-1 mt-0.5">
                <input type={showSecrets ? "text" : "password"}
                  value={token} onChange={(e) => setToken(e.target.value)}
                  placeholder={config?.has_credentials ? "•••••••• (mantém atual)" : "Cole o token aqui"}
                  className="flex-1 bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
                <button type="button" onClick={() => setShowSecrets(s => !s)}
                  className="text-[10px] px-2 border border-noc-border rounded text-noc-muted hover:text-noc-text">
                  {showSecrets ? "👁‍🗨" : "👁"}
                </button>
              </div>
            </label>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-[10px]">
                <span className="text-noc-muted">Usuário</span>
                <input value={username} onChange={(e) => setUsername(e.target.value)}
                  placeholder={config?.has_credentials ? "mantém atual" : "usuário"}
                  className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 mt-0.5" />
              </label>
              <label className="block text-[10px]">
                <span className="text-noc-muted">Senha</span>
                <div className="flex gap-1 mt-0.5">
                  <input type={showSecrets ? "text" : "password"}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder={config?.has_credentials ? "•••••••• (mantém atual)" : "senha"}
                    className="flex-1 bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono" />
                  <button type="button" onClick={() => setShowSecrets(s => !s)}
                    className="text-[10px] px-2 border border-noc-border rounded text-noc-muted hover:text-noc-text">
                    {showSecrets ? "👁‍🗨" : "👁"}
                  </button>
                </div>
              </label>
            </div>
          )}

          <div className="flex items-center gap-3 text-[10px] flex-wrap">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={!tlsVerify}
                onChange={(e) => setTlsVerify(!e.target.checked)} />
              <span className="text-noc-muted">Ignorar TLS (apenas teste; insegura)</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={geocodingEnabled}
                onChange={(e) => setGeocodingEnabled(e.target.checked)} />
              <span className="text-noc-muted">Geocoding endereços (Nominatim)</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)} />
              <span className={enabled ? "text-noc-ok" : "text-noc-muted"}>
                Habilitada {enabled ? "✓" : ""}
              </span>
            </label>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={handleSave} disabled={busy || !configReady || !baseUrl.trim()}
              className="text-[10px] px-3 py-1 rounded bg-noc-purple/15 hover:bg-noc-purple/25 border border-noc-purple/40 text-noc-purple font-semibold disabled:opacity-50">
              {loading ? "..." : (config ? "💾 Salvar" : "+ Criar")}
            </button>
            {config && (
              <button type="button" onClick={handleDelete} disabled={busy}
                className="text-[10px] px-3 py-1 rounded border border-noc-err/30 text-noc-err hover:bg-noc-err/10 disabled:opacity-50">
                🗑 Remover
              </button>
            )}
          </div>
        </div>

        {/* ============ Test + Sync ============ */}
        <div className="space-y-2 bg-noc-bg/30 border border-noc-border rounded p-3">
          <div className="text-xs font-semibold text-noc-text">Sync</div>
          {config?.last_sync_at && (
            <div className="text-[10px] text-noc-muted">
              Último sync: {fmtTime(config.last_sync_at)} · status{" "}
              <span className={STATUS_COLOR[config.last_sync_status] || ""}>
                {config.last_sync_status || "?"}
              </span>
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={handleTest} disabled={busy || !config?.has_credentials}
              className="text-[10px] px-2 py-1 rounded border border-noc-accent/30 text-noc-accent hover:bg-noc-accent/10 disabled:opacity-40 disabled:cursor-not-allowed"
              title={!config?.has_credentials ? t("ixc.tooltipNoCredentials") : ""}>
              {testing ? t("common.busy") : `🔗 ${t("ixc.testConnection")}`}
            </button>
            <button type="button" onClick={() => handleSync("customers")} disabled={busy || !ready}
              className="text-[10px] px-2 py-1 rounded border border-noc-ok/30 text-noc-ok hover:bg-noc-ok/10 disabled:opacity-40 disabled:cursor-not-allowed"
              title={!ready ? t("ixc.tooltipNotReady") : ""}>
              {syncing ? t("common.busy") : `👥 ${t("ixc.syncCustomers")}`}
            </button>
            <button type="button" onClick={() => handleSync("infra")} disabled={busy || !ready}
              className="text-[10px] px-2 py-1 rounded border border-noc-accent/30 text-noc-accent hover:bg-noc-accent/10 disabled:opacity-40 disabled:cursor-not-allowed">
              {syncing ? t("common.busy") : `🗄️ ${t("ixc.syncInfra")}`}
            </button>
            <button type="button" onClick={() => handleSync("full")} disabled={busy || !ready}
              className="text-[10px] px-2 py-1 rounded border border-noc-warn/30 text-noc-warn hover:bg-noc-warn/10 disabled:opacity-40 disabled:cursor-not-allowed">
              {syncing ? t("common.busy") : `🔄 ${t("ixc.syncFull")}`}
            </button>
          </div>

          {testResult && (
            <div className={`text-[10px] rounded p-2 border ${
              testResult.ok
                ? "bg-noc-ok/10 border-noc-ok/30 text-noc-ok"
                : "bg-noc-err/10 border-noc-err/30 text-noc-err"
            }`}>
              {testResult.ok ? "✓" : "✗"} {testResult.message}
              {testResult.duration_ms != null && ` · ${testResult.duration_ms}ms`}
            </div>
          )}

          {lastSync && (
            <div className={`text-[10px] rounded p-2 border ${
              lastSync.result.status === "success"
                ? "bg-noc-ok/10 border-noc-ok/30 text-noc-ok"
                : lastSync.result.status === "partial"
                  ? "bg-noc-warn/10 border-noc-warn/30 text-noc-warn"
                  : "bg-noc-err/10 border-noc-err/30 text-noc-err"
            }`}>
              <div className="font-semibold">
                Sync {lastSync.kind}: {lastSync.result.status}
              </div>
              <div className="font-mono text-[9px] mt-0.5">
                {fmtSummary(lastSync.result.summary)}
              </div>
              {lastSync.result.error_message && (
                <div className="text-[9px] mt-1 opacity-80">
                  ⚠ {lastSync.result.error_message}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ============ Logs ============ */}
        {logs.length > 0 && (
          <div className="space-y-1 bg-noc-bg/30 border border-noc-border rounded p-3">
            <div className="text-xs font-semibold text-noc-text">Últimos logs</div>
            <div className="space-y-0.5 max-h-40 overflow-y-auto">
              {logs.map((l) => (
                <div key={l.id} className="text-[10px] flex items-center gap-2 py-0.5 hover:bg-white/3 px-1 rounded">
                  <span className="font-mono text-noc-muted/80 w-32 truncate">
                    {fmtTime(l.started_at)}
                  </span>
                  <span className="font-mono text-[9px] px-1 py-px rounded bg-noc-border/40">
                    {l.sync_type}
                  </span>
                  <span className={STATUS_COLOR[l.status] || ""}>
                    {l.status}
                  </span>
                  <span className="flex-1 text-noc-muted/80 truncate">
                    {fmtSummary(l.summary)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <button type="button" onClick={onClose}
            disabled={busy}
            className="text-xs px-3 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text disabled:opacity-50">
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
